import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createMember,
  deleteMember,
  getMember,
  getMemberOptions,
  getMembers,
  getUpcomingBirthdaysOfMembers,
  grantMemberShootingClearance,
  getMemberShootingClearances,
  getMemberShootingClearanceHistory,
  inviteToCreateAccount,
  revokeMemberShootingClearance,
  updateMember,
} from '../../api/manageMembersApi';
import { IPaginatedReactQueryParams } from '../interfaces';
import {
  IPaginatedMemberShootingClearancesReactQueryParams,
  IPaginatedMemberShootingClearanceHistoryReactQueryParams,
} from './interfaces';
import { IPaginatedResourceEndpointResponse } from '@api/interfaces';
import i18n from '@translations/i18n';
import {
  ICreateMemberParams,
  IGrantMemberShootingClearanceParams,
  IRevokeMemberShootingClearanceParams,
  IUpdateMemberParams,
  Member,
  MemberBirthday,
  MemberShootingClearance,
  MemberShootingClearanceHistory,
  MembersSort,
} from '../../api/manageMembersApi/interfaces';
import { AxiosError } from 'axios';
import { IDeleteResourceResponse, ValidationErrors } from '@api/interfaces';

export const useGetMembers = ({
  page = 0,
  pageSize,
  sortField,
  search,
  enabled = true,
}: IPaginatedReactQueryParams<MembersSort>) => {
  return useQuery({
    queryKey: ['mng-members', page, pageSize, sortField, search],
    queryFn: () => {
      return getMembers(
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled === true ? true : false,
  });
};

export const useGetMemberOptions = ({
  page = 0,
  pageSize,
  search,
  enabled = true,
}: Omit<IPaginatedReactQueryParams<MembersSort>, 'sortField'>) => {
  return useQuery({
    queryKey: ['mng-member-options', page, pageSize, search],
    queryFn: () => {
      return getMemberOptions(
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        search || undefined
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled === true ? true : false,
  });
};

export const useGetMemberShootingClearancesTable = ({
  memberSlug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedMemberShootingClearancesReactQueryParams) => {
  return useQuery<IPaginatedResourceEndpointResponse<MemberShootingClearance>>({
    queryKey: [
      'mng-member-shooting-clearances',
      memberSlug,
      page,
      pageSize,
      sortField,
      search,
    ],
    queryFn: async () => {
      let rows = await getMemberShootingClearances(memberSlug);

      if (search?.trim()) {
        const query = search.trim().toLowerCase();

        rows = rows.filter((clearance) => {
          const categoryLabel = i18n
            .t(`shooting:clearanceCategories.${clearance.clearanceCategory}`)
            .toLowerCase();

          return (
            clearance.clearanceCategory.toLowerCase().includes(query) ||
            categoryLabel.includes(query) ||
            (clearance.grantedBy?.fullName ?? '')
              .toLowerCase()
              .includes(query)
          );
        });
      }

      if (sortField) {
        const direction = sortField.sort === 'desc' ? -1 : 1;

        rows = [...rows].sort((left, right) => {
          switch (sortField.field) {
            case 'clearanceCategory':
              return (
                left.clearanceCategory.localeCompare(right.clearanceCategory) *
                direction
              );
            case 'clearedOn':
              return left.clearedOn.localeCompare(right.clearedOn) * direction;
            case 'grantedBy':
              return (
                (left.grantedBy?.fullName ?? '').localeCompare(
                  right.grantedBy?.fullName ?? ''
                ) * direction
              );
            default:
              return 0;
          }
        });
      }

      const total = rows.length;
      const perPage =
        pageSize === 'all' ? Math.max(total, 1) : pageSize;
      const start = page * perPage;
      const data = rows.slice(start, start + perPage);

      return {
        data,
        meta: {
          currentPage: page + 1,
          lastPage: Math.max(1, Math.ceil(total / perPage)),
          perPage,
          total,
        },
      };
    },
    enabled: Boolean(memberSlug),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetMemberShootingClearanceHistoryTable = ({
  memberSlug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedMemberShootingClearanceHistoryReactQueryParams) => {
  return useQuery<IPaginatedResourceEndpointResponse<MemberShootingClearanceHistory>>({
    queryKey: [
      'mng-member-shooting-clearance-history',
      memberSlug,
      page,
      pageSize,
      sortField,
      search,
    ],
    queryFn: async () => {
      let rows = await getMemberShootingClearanceHistory(memberSlug);

      if (search?.trim()) {
        const query = search.trim().toLowerCase();

        rows = rows.filter((entry) => {
          const categoryLabel = i18n
            .t(`shooting:clearanceCategories.${entry.clearanceCategory}`)
            .toLowerCase();
          return (
            entry.clearanceCategory.toLowerCase().includes(query) ||
            categoryLabel.includes(query) ||
            (entry.grantedBy?.fullName ?? '').toLowerCase().includes(query) ||
            (entry.revokedBy?.fullName ?? '').toLowerCase().includes(query) ||
            (entry.notes ?? '').toLowerCase().includes(query) ||
            (entry.revokeNotes ?? '').toLowerCase().includes(query)
          );
        });
      }

      if (sortField) {
        const direction = sortField.sort === 'desc' ? -1 : 1;

        rows = [...rows].sort((left, right) => {
          switch (sortField.field) {
            case 'clearanceCategory':
              return (
                left.clearanceCategory.localeCompare(
                  right.clearanceCategory
                ) * direction
              );
            case 'clearedOn':
              return left.clearedOn.localeCompare(right.clearedOn) * direction;
            case 'revokedAt':
              return (
                (left.revokedAt ?? '').localeCompare(right.revokedAt ?? '') *
                direction
              );
            case 'revokedBy':
              return (
                (left.revokedBy?.fullName ?? '').localeCompare(
                  right.revokedBy?.fullName ?? ''
                ) * direction
              );
            case 'grantedBy':
              return (
                (left.grantedBy?.fullName ?? '').localeCompare(
                  right.grantedBy?.fullName ?? ''
                ) * direction
              );
            default:
              return 0;
          }
        });
      }

      const total = rows.length;
      const perPage =
        pageSize === 'all' ? Math.max(total, 1) : pageSize;
      const start = page * perPage;
      const data = rows.slice(start, start + perPage);

      return {
        data,
        meta: {
          currentPage: page + 1,
          lastPage: Math.max(1, Math.ceil(total / perPage)),
          perPage,
          total,
        },
      };
    },
    enabled: Boolean(memberSlug),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGrantMemberShootingClearanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    MemberShootingClearance,
    AxiosError<ValidationErrors>,
    { memberSlug: string; params: IGrantMemberShootingClearanceParams }
  >({
    mutationFn: ({ memberSlug, params }) =>
      grantMemberShootingClearance(memberSlug, params),
    onSuccess: (_data, { memberSlug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-member-shooting-clearances', memberSlug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-member-shooting-clearance-history', memberSlug],
      });
    },
  });
};

export const useRevokeMemberShootingClearanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError<ValidationErrors>,
    { memberSlug: string; params: IRevokeMemberShootingClearanceParams }
  >({
    mutationFn: ({ memberSlug, params }) =>
      revokeMemberShootingClearance(memberSlug, params),
    onSuccess: (_data, { memberSlug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-member-shooting-clearances', memberSlug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-member-shooting-clearance-history', memberSlug],
      });
    },
  });
};

export const useGetMember = (slug?: string) => {
  return useQuery<Member>({
    queryFn: () => getMember(slug || ''),
    queryKey: ['mng-member', slug],
    enabled: !!slug,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreatMemberMutation = () => {
  return useMutation<string, AxiosError<ValidationErrors>, ICreateMemberParams>(
    {
      mutationFn: createMember,
    }
  );
};

export const useInviteMemberToCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    mutationFn: inviteToCreateAccount,
    onSuccess: (_data, slug) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-member', slug],
      });
    },
  });
};

export const useUpdateMemberMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    string,
    AxiosError<ValidationErrors>,
    { slug: string; memberData: IUpdateMemberParams }
  >({
    mutationFn: async ({ slug, memberData }) => updateMember(slug, memberData),
    onSuccess: (slug: string) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-member', slug],
      });
    },
  });
};

export const useDeleteMemberMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<IDeleteResourceResponse, AxiosError, string>({
    mutationFn: deleteMember,
    onSuccess: (_, slug) => {
      queryClient.removeQueries({
        queryKey: ['mng-member', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-members'],
      });
    },
  });
};

export const useGetUpcomingBirthdaysOfMembers = () => {
  return useQuery<MemberBirthday[]>({
    queryFn: () => getUpcomingBirthdaysOfMembers(),
    queryKey: ['member-birthdays'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};
